import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IoChevronBack, IoEye, IoEyeOff, IoLockClosed, IoMail, IoPhonePortrait, IoTicket } from "react-icons/io5";

import { login, register } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { assetPath } from "../utils/assets";

const PHONE_MIN_LENGTH = 8;
const PHONE_MAX_LENGTH = 15;
const PHONE_LOCAL_MIN_LENGTH = 6;
const PHONE_LOCAL_MAX_LENGTH = 13;
const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_MAX_LENGTH = 20;
const REFERRAL_MAX_LENGTH = 64;
const REFERRAL_PATTERN = /^[A-Za-z0-9_-]+$/;
const EMAIL_MAX_LENGTH = 254;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COUNTRY_CODE_OPTIONS = [
    { code: "60", label: "MY +60", name: "Malaysia" },
    { code: "62", label: "ID +62", name: "Indonesia" },
    { code: "65", label: "SG +65", name: "Singapore" },
    { code: "66", label: "TH +66", name: "Thailand" },
    { code: "84", label: "VN +84", name: "Vietnam" },
    { code: "855", label: "KH +855", name: "Cambodia" },
    { code: "86", label: "CN +86", name: "China" },
    { code: "81", label: "JP +81", name: "Japan" },
    { code: "82", label: "KR +82", name: "South Korea" },
    { code: "91", label: "IN +91", name: "India" },
];

const DEFAULT_COUNTRY_CODE = "62";

const normalizePhoneWithCountryCode = (phoneNumber, countryCode) => {
    const dialCode = String(countryCode || "").replace(/\D/g, "");
    const digits = String(phoneNumber || "").replace(/\D/g, "");
    let localNumber = digits;

    if (dialCode && localNumber.startsWith(dialCode)) {
        localNumber = localNumber.slice(dialCode.length);
    }

    localNumber = localNumber.replace(/^0+/, "");

    return {
        localNumber,
        fullPhoneNumber: `${dialCode}${localNumber}`,
    };
};

const Register = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const { token, setAuthToken } = useAuth();

    const [form, setForm] = useState({
        countryCode: DEFAULT_COUNTRY_CODE,
        phoneNumber: "",
        email: "",
        password: "",
        confirmPassword: "",
        referralCode: "",
    });
    const [errorMessage, setErrorMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [authRedirectPath, setAuthRedirectPath] = useState("");

    useEffect(() => {
        if (errorMessage) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [errorMessage]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const referral = params.get("start") || params.get("referral");

        if (!referral) {
            return;
        }

        setForm((prev) => {
            if (prev.referralCode === referral) {
                return prev;
            }

            return {
                ...prev,
                referralCode: referral,
            };
        });
    }, [location.search]);

    if (authRedirectPath) {
        return <Navigate to={authRedirectPath} replace />;
    }

    if (token) {
        return <Navigate to="/" replace />;
    }

    const handleChange = (key) => (event) => {
        let nextValue = event.target.value;

        if (key === "phoneNumber") {
            nextValue = nextValue.replace(/\D/g, "");
        }

        if (key === "countryCode") {
            nextValue = nextValue.replace(/\D/g, "") || DEFAULT_COUNTRY_CODE;
        }

        if (key === "referralCode") {
            nextValue = nextValue.replace(/\s+/g, "").trim();
        }

        if (key === "email") {
            nextValue = nextValue.trim();
        }

        setForm((prev) => ({
            ...prev,
            [key]: nextValue,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage("");

        const phoneNumber = (form.phoneNumber || "").trim();
        const sanitizedPhoneNumber = phoneNumber.replace(/\D/g, "");
        const { localNumber, fullPhoneNumber } = normalizePhoneWithCountryCode(
            sanitizedPhoneNumber,
            form.countryCode
        );
        const email = (form.email || "").trim();
        const password = form.password || "";
        const confirmPassword = form.confirmPassword || "";
        const referralCode = (form.referralCode || "").replace(/\s+/g, "").trim();

        if (phoneNumber !== sanitizedPhoneNumber) {
            setErrorMessage(t("invalidPhoneNumber"));
            return;
        }

        if (
            localNumber.length < PHONE_LOCAL_MIN_LENGTH ||
            localNumber.length > PHONE_LOCAL_MAX_LENGTH ||
            fullPhoneNumber.length < PHONE_MIN_LENGTH ||
            fullPhoneNumber.length > PHONE_MAX_LENGTH
        ) {
            setErrorMessage(t("invalidPhoneNumber"));
            return;
        }

        if (!EMAIL_PATTERN.test(email) || email.length > EMAIL_MAX_LENGTH) {
            setErrorMessage(t("invalidEmail"));
            return;
        }

        if (password.length < PASSWORD_MIN_LENGTH || password.length > PASSWORD_MAX_LENGTH) {
            setErrorMessage(t("invalidPasswordLength", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH }));
            return;
        }

        if (password !== confirmPassword) {
            setErrorMessage(t("passwordsDoNotMatch"));
            return;
        }

        if (referralCode) {
            if (referralCode.length > REFERRAL_MAX_LENGTH || !REFERRAL_PATTERN.test(referralCode)) {
                setErrorMessage(t("invalidReferralCode"));
                return;
            }
        }

        setSubmitting(true);

        try {
            const registerResponse = await register({
                phone_number: fullPhoneNumber,
                email,
                password: password || "",
                language: i18n.language,
                referral_code: referralCode,
            });

            if (!registerResponse.success) {
                setErrorMessage(registerResponse.data || t("pleaseTryLater"));
                return;
            }

            const loginResponse = await login({
                phoneNumber: registerResponse.data || fullPhoneNumber,
                password,
            });

            if (loginResponse.success && loginResponse.data) {
                const nextPath = Number(loginResponse?.web_password_set) === 0 ? "/change-password" : "/";
                setAuthToken(loginResponse.data, registerResponse.data || fullPhoneNumber, loginResponse.language);
                setAuthRedirectPath(nextPath);
            } else {
                setErrorMessage(loginResponse.data || t("pleaseTryLater"));
            }
        } catch (error) {
            setErrorMessage(t("pleaseTryLater"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="relative min-h-screen overflow-hidden bg-[#031523] text-white">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(17,155,255,0.2)_0%,_transparent_34%),linear-gradient(180deg,_#07253A_0%,_#031523_42%,_#02111A_100%)]" />
                <div className="absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-[#00D9FF]/10 blur-3xl" />
            </div>

            <div className="relative mx-auto flex min-h-screen w-full max-w-7xl items-center justify-center px-3 py-2 sm:px-4 sm:py-8 lg:px-8">
                <div className="w-full max-w-[460px]">
                    <div className="mb-2 flex items-center justify-between sm:mb-5">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white sm:gap-2 sm:px-3 sm:py-2 sm:text-xs sm:tracking-[0.16em]"
                        >
                            <IoChevronBack className="text-sm" />
                            <span>{t("backToLobby")}</span>
                        </Link>

                        <img src={assetPath("/icons/logo.png")} alt="Indo368" className="w-[104px] sm:w-[128px]" />
                    </div>

                    <div className="rounded-[1.25rem] bg-[#082032]/92 p-3 shadow-[0_32px_90px_-54px_rgba(0,0,0,0.9)] ring-1 ring-[#0E4F77] backdrop-blur-xl sm:rounded-[2rem] sm:p-7">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.18em] text-[#84C9E5]/70 sm:text-[11px] sm:tracking-[0.28em]">
                                {t("register")}
                            </p>
                            <h1 className="mt-1 text-xl font-black text-white sm:mt-3 sm:text-3xl">
                                {t("createAccount")}
                            </h1>
                        </div>

                        {errorMessage && (
                            <div className="mt-3 rounded-[1rem] bg-[#3A1318] px-3 py-2 text-xs text-white ring-1 ring-[#7E2D37] sm:mt-5 sm:rounded-[1.2rem] sm:px-4 sm:py-3 sm:text-sm">
                                {errorMessage}
                            </div>
                        )}

                        <form className="mt-3 space-y-2 sm:mt-6 sm:space-y-4" onSubmit={handleSubmit}>
                            <label className="block" htmlFor="register-phone">
                                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#84C9E5]/72 sm:mb-2 sm:gap-2 sm:text-[11px] sm:tracking-[0.22em]">
                                    <IoPhonePortrait className="text-sm" />
                                    {t("phoneNumber")}
                                </span>
                                <div className="flex items-center rounded-[1rem] bg-[#061A2A] px-3 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF] sm:rounded-[1.3rem] sm:px-4">
                                    <select
                                        id="register-country-code"
                                        value={form.countryCode}
                                        onChange={handleChange("countryCode")}
                                        className="h-9 shrink-0 bg-transparent pr-2 text-xs font-bold text-white outline-none sm:h-14 sm:pr-3 sm:text-sm"
                                        aria-label={t("countryCode")}
                                        autoComplete="tel-country-code"
                                    >
                                        {COUNTRY_CODE_OPTIONS.map((country) => (
                                            <option key={country.code} value={country.code} className="bg-[#061A2A] text-white">
                                                {country.label}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="mr-2 h-5 w-px bg-white/12 sm:mr-3 sm:h-6" />
                                    <input
                                        id="register-phone"
                                        type="tel"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={PHONE_LOCAL_MAX_LENGTH}
                                        value={form.phoneNumber}
                                        onChange={handleChange("phoneNumber")}
                                        className="h-9 w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30 sm:h-14 sm:text-base"
                                        placeholder="123456789"
                                        autoComplete="username"
                                    />
                                </div>
                            </label>

                            <label className="block" htmlFor="register-email">
                                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#84C9E5]/72 sm:mb-2 sm:gap-2 sm:text-[11px] sm:tracking-[0.22em]">
                                    <IoMail className="text-sm" />
                                    {t("email")}
                                </span>
                                <div className="flex items-center rounded-[1rem] bg-[#061A2A] px-3 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF] sm:rounded-[1.3rem] sm:px-4">
                                    <input
                                        id="register-email"
                                        type="email"
                                        inputMode="email"
                                        maxLength={EMAIL_MAX_LENGTH}
                                        value={form.email}
                                        onChange={handleChange("email")}
                                        className="h-9 w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30 sm:h-14 sm:text-base"
                                        placeholder={t("enterEmail")}
                                        autoComplete="email"
                                    />
                                </div>
                            </label>

                            <label className="block" htmlFor="register-password">
                                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#84C9E5]/72 sm:mb-2 sm:gap-2 sm:text-[11px] sm:tracking-[0.22em]">
                                    <IoLockClosed className="text-sm" />
                                    {t("password")}
                                </span>
                                <div className="flex items-center rounded-[1rem] bg-[#061A2A] px-3 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF] sm:rounded-[1.3rem] sm:px-4">
                                    <input
                                        id="register-password"
                                        type={showPassword ? "text" : "password"}
                                        minLength={PASSWORD_MIN_LENGTH}
                                        maxLength={PASSWORD_MAX_LENGTH}
                                        value={form.password}
                                        onChange={handleChange("password")}
                                        className="h-9 w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30 sm:h-14 sm:text-base"
                                        placeholder={t("createPassword")}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        className="text-white/55 transition-colors duration-300 hover:text-white"
                                        aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                                    >
                                        {showPassword ? <IoEyeOff className="text-lg" /> : <IoEye className="text-lg" />}
                                    </button>
                                </div>
                            </label>

                            <label className="block" htmlFor="register-confirm-password">
                                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#84C9E5]/72 sm:mb-2 sm:gap-2 sm:text-[11px] sm:tracking-[0.22em]">
                                    <IoLockClosed className="text-sm" />
                                    {t("confirmPassword")}
                                </span>
                                <div className="flex items-center rounded-[1rem] bg-[#061A2A] px-3 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF] sm:rounded-[1.3rem] sm:px-4">
                                    <input
                                        id="register-confirm-password"
                                        type={showConfirmPassword ? "text" : "password"}
                                        minLength={PASSWORD_MIN_LENGTH}
                                        maxLength={PASSWORD_MAX_LENGTH}
                                        value={form.confirmPassword}
                                        onChange={handleChange("confirmPassword")}
                                        className="h-9 w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30 sm:h-14 sm:text-base"
                                        placeholder={t("confirmCreatePassword")}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                        className="text-white/55 transition-colors duration-300 hover:text-white"
                                        aria-label={showConfirmPassword ? t("hidePassword") : t("showPassword")}
                                    >
                                        {showConfirmPassword ? <IoEyeOff className="text-lg" /> : <IoEye className="text-lg" />}
                                    </button>
                                </div>
                            </label>

                            <label className="block" htmlFor="register-referral">
                                <span className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#84C9E5]/72 sm:mb-2 sm:gap-2 sm:text-[11px] sm:tracking-[0.22em]">
                                    <IoTicket className="text-sm" />
                                    {t("referralCode")}
                                </span>
                                <div className="flex items-center rounded-[1rem] bg-[#061A2A] px-3 ring-1 ring-[#0D3853] transition-colors duration-300 focus-within:ring-[#21C8FF] sm:rounded-[1.3rem] sm:px-4">
                                    <input
                                        id="register-referral"
                                        type="text"
                                        maxLength={REFERRAL_MAX_LENGTH}
                                        value={form.referralCode}
                                        onChange={handleChange("referralCode")}
                                        className="h-9 w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-white/30 sm:h-14 sm:text-base"
                                        placeholder={t("optional")}
                                    />
                                </div>
                            </label>

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full rounded-full bg-[linear-gradient(180deg,_#A0F8FF_0%,_#00EDFF_38%,_#00B8EE_100%)] px-5 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-slate-950 shadow-[0_20px_40px_-28px_rgba(0,151,201,1)] transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:px-6 sm:py-3.5 sm:text-sm sm:tracking-[0.18em]"
                            >
                                {submitting ? t("loading") : t("register")}
                            </button>
                        </form>

                        <div className="mt-3 border-t border-white/8 pt-3 sm:mt-6 sm:pt-5">
                            <p className="text-xs text-white/62 sm:text-sm">
                                {t("alreadyHaveAccount")}{" "}
                                <Link to="/login" className="font-bold text-[#79EAFF] transition-colors duration-300 hover:text-white">
                                    {t("login")}
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Register;